/**
 * ConfigErrorScreen — last-resort fallback when env validation fails.
 *
 * Why this exists: round 25's broken production AAB shipped to Play Store
 * with missing EXPO_PUBLIC_* env vars. The previous `lib/env.ts` threw at
 * module load → React Native bridge died on Android NewArch+Hermes (Expo
 * issue #41543) → blank gray screen → unrecoverable because expo-updates
 * couldn't apply the OTA fix before the JS crashed.
 *
 * The defensive `lib/env.ts` (round 25) now returns `isMisconfigured = true`
 * with a `configError` string instead of throwing. The root layout renders
 * THIS screen as an early return, BEFORE any provider that would try to use
 * the placeholder Supabase URL.
 *
 * Hard constraint: this component cannot depend on:
 *   - React Context providers (QueryClientProvider, ThemeProvider, NavThemeProvider)
 *     because we render BEFORE they mount.
 *   - i18n/translation hooks — keep strings inline. If i18n config also fails
 *     to load for any reason, we still need to render something readable.
 *   - Theme tokens that depend on a Zustand store — render even if the store
 *     hasn't bootstrapped yet.
 *   - Our custom Pressable (which depends on RNGH context) — use plain RN
 *     Pressable since GestureHandlerRootView mounts AFTER our early return.
 *
 * What it offers the user:
 *   1. A clear "the app is updating, hang tight" message (Spanish + English).
 *   2. A button to manually trigger an OTA check + reload — gives the user
 *      a way out if the auto-update on app foreground hasn't fired yet.
 *   3. A link to app.tago.rent so the user can use the web app right now.
 *   4. In __DEV__, the actual config error string so engineers debugging
 *      logcat can see exactly which env var is missing.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  AccessibilityInfo,
  Animated,
  Easing,
  Linking,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native'
import { Image } from 'expo-image'
import { LinearGradient } from 'expo-linear-gradient'
import * as Localization from 'expo-localization'
import * as Updates from 'expo-updates'
import { otaClient } from '../../lib/updates/ota-client'
import { ExternalLink, RefreshCw } from '@/components/ui/icons'
import { COLORS, FONTS, GRADIENTS, RADIUS, SPACING } from '../../lib/brand'
import {
  impact,
  ImpactStyle,
  notification,
  NotificationType,
  selection,
} from '../../lib/utils/haptics'
import { DURATION } from '../../lib/theme/tokens'

// ── Bilingual strings (no i18n dependency) ─────────────────────────────
const STRINGS = {
  es: {
    title: 'Actualizando Tago',
    description: 'Estamos descargando la última versión. Esto puede tomar unos segundos.',
    checkButton: 'Buscar actualización',
    checking: 'Buscando…',
    downloading: 'Descargando…',
    restarting: 'Aplicando…',
    upToDate: 'No hay actualización disponible. Volvé a intentarlo en unos segundos.',
    failed: 'No se pudo conectar. Verificá tu internet.',
    webFallback: 'O usá la app web',
    debugLabel: 'Detalles del error (solo desarrollo):',
  },
  en: {
    title: 'Updating Tago',
    description: "We're downloading the latest version. This may take a few seconds.",
    checkButton: 'Check for update',
    checking: 'Checking…',
    downloading: 'Downloading…',
    restarting: 'Applying…',
    upToDate: 'No update available. Try again in a few seconds.',
    failed: 'Could not connect. Check your internet.',
    webFallback: 'Or use the web app',
    debugLabel: 'Error details (dev only):',
  },
} as const

const WEB_FALLBACK_URL = 'https://app.tago.rent'

interface ConfigErrorScreenProps {
  /** The configError string from lib/env.ts. Shown in __DEV__ for debugging. */
  error: string | null
}

type Status = 'idle' | 'checking' | 'downloading' | 'restarting' | 'upToDate' | 'failed'

function pickLanguage(): 'es' | 'en' {
  // Defensive: expo-localization could conceivably fail. Default to Spanish
  // since this is a Salvadoran app.
  try {
    const locale = Localization.getLocales()[0]?.languageCode
    return locale === 'en' ? 'en' : 'es'
  } catch {
    return 'es'
  }
}

export function ConfigErrorScreen({ error }: ConfigErrorScreenProps) {
  const [status, setStatus] = useState<Status>('idle')
  const spin = useRef(new Animated.Value(0)).current
  const pulse = useRef(new Animated.Value(0)).current
  const cycleHaptics = useRef(0)
  const [cycleIndex, setCycleIndex] = useState(0)
  const lang = pickLanguage()
  const t = STRINGS[lang]
  const isWorking = status === 'checking' || status === 'downloading' || status === 'restarting'
  const steps = useMemo(() => [t.checking, t.downloading, t.restarting], [t])

  // This screen deliberately uses the stock RN Animated API rather than
  // Reanimated — it is the last line of defence when the app is misconfigured,
  // so it stays on the smallest possible dependency surface. The trade-off is
  // that nothing here honours reduced motion for free (Reanimated defaults
  // every animation to ReduceMotion.System; RN's Animated does not), so the
  // setting is read explicitly and the spin/pulse loops never start.
  const [reducedMotion, setReducedMotion] = useState(false)

  useEffect(() => {
    let active = true
    void AccessibilityInfo.isReduceMotionEnabled().then((enabled) => {
      if (active) setReducedMotion(enabled)
    })
    const subscription = AccessibilityInfo.addEventListener('reduceMotionChanged', (enabled) => {
      setReducedMotion(enabled)
    })
    return () => {
      active = false
      subscription.remove()
    }
  }, [])

  useEffect(() => {
    if (!isWorking) {
      spin.stopAnimation()
      pulse.stopAnimation()
      spin.setValue(0)
      pulse.setValue(0)
      cycleHaptics.current = 0
      setCycleIndex(0)
      return undefined
    }

    // Reduced motion silences the decorative loops but NOT the step cycling
    // below: "checking / downloading / restarting" is the only progress the
    // user gets on this screen, so it is information, not decoration.
    if (reducedMotion) {
      spin.stopAnimation()
      pulse.stopAnimation()
      spin.setValue(0)
      pulse.setValue(0)
    }

    const spinLoop = Animated.loop(
      Animated.timing(spin, {
        toValue: 1,
        duration: DURATION.verySlow,
        easing: Easing.linear,
        useNativeDriver: true,
      }),
    )
    const pulseLoop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, {
          toValue: 1,
          duration: DURATION.verySlow,
          easing: Easing.out(Easing.quad),
          useNativeDriver: true,
        }),
        Animated.timing(pulse, {
          toValue: 0,
          duration: DURATION.verySlow,
          easing: Easing.in(Easing.quad),
          useNativeDriver: true,
        }),
      ]),
    )

    impact(ImpactStyle.Light)
    if (!reducedMotion) {
      spinLoop.start()
      pulseLoop.start()
    }

    const interval = setInterval(() => {
      setCycleIndex((value) => (value + 1) % steps.length)
      if (cycleHaptics.current < 6) {
        cycleHaptics.current += 1
        selection()
      }
    }, 850)

    return () => {
      clearInterval(interval)
      spinLoop.stop()
      pulseLoop.stop()
    }
  }, [isWorking, pulse, reducedMotion, spin, steps.length])

  const handleCheckUpdate = async () => {
    impact(ImpactStyle.Medium)
    // expo-updates is a no-op on web and in Expo Go / dev. Open the web app
    // as a fallback in those environments.
    if (Platform.OS === 'web' || !Updates.isEnabled) {
      void Linking.openURL(WEB_FALLBACK_URL)
      return
    }

    setStatus('checking')
    try {
      const result = await otaClient.check()
      if (!result.isAvailable) {
        setStatus('upToDate')
        notification(NotificationType.Warning)
        return
      }
      setStatus('downloading')
      const fetchResult = await otaClient.fetch()
      if (fetchResult.isNew) {
        setStatus('restarting')
        // reloadAsync replaces the running JS bundle with the freshly
        // downloaded one. After this, env.ts re-runs with the new code,
        // hopefully populated env vars, and the app boots normally.
        await Updates.reloadAsync()
      } else {
        setStatus('upToDate')
        notification(NotificationType.Warning)
      }
    } catch {
      setStatus('failed')
      notification(NotificationType.Error)
    }
  }

  const handleWebFallback = () => {
    impact(ImpactStyle.Light)
    void Linking.openURL(WEB_FALLBACK_URL)
  }

  const buttonLabel =
    status === 'checking'
      ? t.checking
      : status === 'downloading'
        ? t.downloading
        : status === 'restarting'
          ? t.restarting
          : t.checkButton

  const statusMessage = status === 'upToDate' ? t.upToDate : status === 'failed' ? t.failed : null
  const spinStyle = {
    transform: [
      {
        rotate: spin.interpolate({
          inputRange: [0, 1],
          outputRange: ['0deg', '360deg'],
        }),
      },
    ],
  }
  const haloStyle = {
    opacity: pulse.interpolate({
      inputRange: [0, 1],
      outputRange: [0.2, 0.58],
    }),
    transform: [
      {
        scale: pulse.interpolate({
          inputRange: [0, 1],
          outputRange: [0.96, 1.14],
        }),
      },
    ],
  }

  return (
    <LinearGradient
      colors={[...GRADIENTS.primarySmooth.colors] as [string, string, ...string[]]}
      start={GRADIENTS.primarySmooth.start}
      end={GRADIENTS.primarySmooth.end}
      style={styles.container}
    >
      <View style={styles.content}>
        <Image
          source={require('../../assets/images/logo-stacked.png')}
          style={styles.logo}
          contentFit="contain"
          accessibilityIgnoresInvertColors
        />

        <Text style={styles.title} accessibilityRole="header">
          {t.title}
        </Text>

        <Text style={styles.description}>{t.description}</Text>

        <View style={styles.reloadStatus} accessibilityLiveRegion="polite">
          {isWorking ? (
            <Animated.View pointerEvents="none" style={[styles.reloadHalo, haloStyle]} />
          ) : null}
          <Animated.View style={isWorking ? spinStyle : undefined}>
            <RefreshCw size={25} color={COLORS.white} strokeWidth={1.8} />
          </Animated.View>
        </View>

        {isWorking ? (
          <View
            style={styles.cycleDots}
            accessibilityElementsHidden
            importantForAccessibility="no-hide-descendants"
          >
            {steps.map((step, index) => (
              <View
                key={step}
                style={[styles.cycleDot, cycleIndex === index && styles.cycleDotActive]}
              />
            ))}
          </View>
        ) : null}

        <Pressable
          accessibilityRole="button"
          accessibilityLabel={buttonLabel}
          accessibilityState={{ disabled: isWorking }}
          onPress={handleCheckUpdate}
          disabled={isWorking}
          style={({ pressed }) => [
            styles.button,
            pressed && styles.buttonPressed,
            isWorking && styles.buttonDisabled,
          ]}
        >
          <Animated.View style={isWorking ? spinStyle : undefined}>
            <RefreshCw size={18} color={COLORS.white} strokeWidth={2} />
          </Animated.View>
          <Text style={styles.buttonLabel}>{buttonLabel}</Text>
        </Pressable>

        {statusMessage ? (
          <Text style={styles.statusMessage} accessibilityLiveRegion="polite">
            {statusMessage}
          </Text>
        ) : null}

        <Pressable
          accessibilityRole="link"
          accessibilityLabel={t.webFallback}
          onPress={handleWebFallback}
          style={({ pressed }) => [styles.webLink, pressed && styles.webLinkPressed]}
        >
          <Text style={styles.webLinkLabel}>{t.webFallback}</Text>
          <View style={styles.webLinkUrlRow}>
            <Text style={styles.webLinkUrl}>app.tago.rent</Text>
            <ExternalLink size={14} color={COLORS.white} strokeWidth={2} />
          </View>
        </Pressable>

        {__DEV__ && error ? (
          <View style={styles.debugBox}>
            <Text style={styles.debugLabel}>{t.debugLabel}</Text>
            <Text selectable style={styles.debugText}>
              {error}
            </Text>
          </View>
        ) : null}
      </View>
    </LinearGradient>
  )
}

const LOGO_SIZE = 120

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: SPACING.xl,
  },
  content: {
    alignItems: 'center',
    maxWidth: 360,
    width: '100%',
  },
  logo: {
    width: LOGO_SIZE,
    height: LOGO_SIZE,
    marginBottom: SPACING.xl,
  },
  title: {
    fontFamily: FONTS.bodyBold,
    fontSize: 24,
    color: COLORS.white,
    textAlign: 'center',
    marginBottom: SPACING.sm,
  },
  description: {
    fontFamily: FONTS.body,
    fontSize: 15,
    lineHeight: 22,
    color: 'rgba(255, 255, 255, 0.85)',
    textAlign: 'center',
    marginBottom: SPACING.lg,
  },
  reloadStatus: {
    width: 62,
    height: 62,
    borderRadius: 31,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.14)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.24)',
    marginBottom: SPACING.sm,
  },
  reloadHalo: {
    position: 'absolute',
    width: 82,
    height: 82,
    borderRadius: 41,
    backgroundColor: 'rgba(255, 255, 255, 0.12)',
  },
  cycleDots: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: SPACING.xs,
    minHeight: 12,
    marginBottom: SPACING.md,
  },
  cycleDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
    backgroundColor: 'rgba(255,255,255,0.32)',
  },
  cycleDotActive: {
    width: 18,
    backgroundColor: COLORS.amber.DEFAULT,
  },
  button: {
    minHeight: 48,
    minWidth: 220,
    paddingHorizontal: SPACING.xl,
    paddingVertical: SPACING.md,
    borderRadius: RADIUS.full,
    backgroundColor: COLORS.coral.dark,
    flexDirection: 'row',
    gap: SPACING.sm,
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonPressed: {
    opacity: 0.85,
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  buttonLabel: {
    fontFamily: FONTS.bodyBold,
    fontSize: 16,
    color: COLORS.white,
  },
  statusMessage: {
    fontFamily: FONTS.body,
    fontSize: 13,
    color: 'rgba(255, 255, 255, 0.85)',
    textAlign: 'center',
    marginTop: SPACING.md,
    maxWidth: 280,
  },
  webLink: {
    marginTop: SPACING.xl,
    alignItems: 'center',
    paddingVertical: SPACING.sm,
    paddingHorizontal: SPACING.md,
  },
  webLinkPressed: {
    opacity: 0.7,
  },
  webLinkLabel: {
    fontFamily: FONTS.body,
    fontSize: 13,
    color: 'rgba(255, 255, 255, 0.7)',
  },
  webLinkUrl: {
    fontFamily: FONTS.bodyBold,
    fontSize: 15,
    color: COLORS.white,
    textDecorationLine: 'underline',
  },
  webLinkUrlRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
    marginTop: SPACING.xs,
  },
  debugBox: {
    marginTop: SPACING['2xl'],
    padding: SPACING.md,
    borderRadius: RADIUS.sm,
    backgroundColor: 'rgba(0, 0, 0, 0.35)',
    width: '100%',
  },
  debugLabel: {
    fontFamily: FONTS.bodyBold,
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.7)',
    marginBottom: SPACING.xs,
  },
  debugText: {
    fontFamily: FONTS.mono,
    fontSize: 12,
    lineHeight: 16,
    color: 'rgba(255, 255, 255, 0.85)',
  },
})
